// src/features/executive/account/widgets/ExpenseCategoryCard.tsx
//
// Raw catalog view: one slice per `expense_types` row that has spend in the
// selected period. No keyword folding — every distinct type is its own slice.

import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useExpenseCategory } from '../hooks/use-expense-category'
import { formatCurrency } from 'utils/executive.utils'
import { paletteColor } from 'features/executive/shared/utils/palette'
import { shouldUseBar, sortByValueDesc } from 'features/executive/shared/utils/chart-mode'
import CategoricalBar, { CategoricalBarRow } from 'features/executive/insight/widgets/CategoricalBar'

ChartJS.register(ArcElement, ChartTooltip, Legend)

export default function ExpenseCategoryCard() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useExpenseCategory()

  const isEmpty = !data || data.total === 0

  const useBar = !isEmpty && shouldUseBar(data?.categories.length ?? 0)

  const barRows: CategoricalBarRow[] = data
    ? sortByValueDesc(
        data.categories.map((c, i) => ({
          key: String(c.id),
          label: c.name,
          count: c.amount,
          percent: c.percent,
          color: paletteColor(i),
        })),
        (r) => r.count,
      )
    : []

  const chartData = useMemo(() => {
    if (!data) return null
    return {
      labels: data.categories.map((c) => c.name),
      datasets: [
        {
          data: data.categories.map((c) => c.amount),
          backgroundColor: data.categories.map((_, i) => paletteColor(i)),
        },
      ],
    }
  }, [data])

  if (useBar) {
    return (
      <CategoricalBar
        title={t('EXECUTIVE.ACCOUNT.EXPENSE_CATEGORY.TITLE')}
        titleTooltip={t('EXECUTIVE.ACCOUNT.EXPENSE_CATEGORY.TOOLTIP')}
        subtitle={`${t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.EXPENSE')}: ${formatCurrency(data?.total ?? 0)}`}
        rows={barRows}
        initialVisible={5}
        formatValue={(r) => `${formatCurrency(r.count)} (${r.percent.toFixed(0)}%)`}
        emptyMessage={t('EXECUTIVE.ACCOUNT.EXPENSE_CATEGORY.EMPTY', 'ไม่พบค่าใช้จ่ายในช่วงที่เลือก')}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    )
  }

  return (
    <WidgetCard
      title={t('EXECUTIVE.ACCOUNT.EXPENSE_CATEGORY.TITLE')}
      titleTooltip={t('EXECUTIVE.ACCOUNT.EXPENSE_CATEGORY.TOOLTIP')}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={380}
    >
      {data && (
        <>
          <Box sx={{ height: 200, position: 'relative', mb: 2 }}>
            <Doughnut
              data={chartData as any}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                cutout: '70%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${ctx.label}: ${formatCurrency(Number(ctx.parsed))} (${(
                          (Number(ctx.parsed) / data.total) *
                          100
                        ).toFixed(1)}%)`,
                    },
                  },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.EXPENSE')}
              </Typography>
              <Typography variant="h6">{formatCurrency(data.total)}</Typography>
            </Box>
          </Box>
          <Stack spacing={0.5}>
            {data.categories.map((c, i) => (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: paletteColor(i),
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={c.name}
                >
                  {c.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(c.amount)} ({c.percent.toFixed(0)}%)
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </WidgetCard>
  )
}
